import React from 'react'
import { Page, RewardCta, RewardImage, RewardInfoContent, RewardInfoWrapper } from '@/components'
import Header from '@/components/RewardDetails/Header'
import { H1, Spinner, Text, View, useMedia } from '@red-ui/components'
import { PUBLIC_URL } from '@/config'
import { layout } from '@virgin-core/styles'
import ContentfulCopy from '@/components/richText/contentfulCopy'
import { BLOCKS, Document, TopLevelBlock } from '@contentful/rich-text-types'
import { useAppSelector } from '@/redux/hooks/hooks'
import { getFeatureIsLoading, useFeatureSelector } from '@vrw/data/src/redux/features/features.selectors'
import { FeatureName } from '@vrw/data/src/redux/features/features.types'
import { Redirect } from '@/router/redirect'
import { PATHS } from '@/router/paths'
import { PointsPicker } from '../components/PointsPicker'
import { getString } from '@vrw/data'
import { formatPoints } from '@vrw/data/src/utils/formatters'

const BONUS_POINTS = 50

export const BiltLandingPage = () => {
  const media = useMedia()
  const isEnabled = useAppSelector(useFeatureSelector(FeatureName.WEB_BILT))
  const featureFlagsLoading = useAppSelector(getFeatureIsLoading)
  const balance = 120000 // replace with actual balance from state or API */

  if (featureFlagsLoading) return <Spinner size="large" color="$red6" height="100vh" justifyContent="center" />

  if (!isEnabled) return <Redirect to={PATHS.HOME} />

  return (
    <>
      <Header disableCurve={media.mobile} position="absolute" left="$0" right="$0" top="$0">
        <View $gtMobile={{ height: 227 }} $gtTabletLandscape={{ height: 387 }} />
      </Header>
      <Page title={bilt.content.title}>
        <View
          style={{ justifySelf: 'center' }}
          marginInline="auto"
          width="100%"
          $gtTabletPortrait={{ width: layout.heroWidth.tablet }}
          $gtTabletLandscape={{ marginTop: '$7', width: layout.heroWidth.desktop }}>
          <RewardImage
            rewardImageUrl={bilt.content.imageUrl}
            brandLogoUrl={bilt.content.brandDetails.brandLogo}
            rewardImageAlt={bilt.content.title}
            brandLogoAlt={bilt.content.brandDetails.brandName}
          />
        </View>
        <RewardInfoWrapper>
          <RewardInfoContent>
            <H1
              display="block"
              fontStyle="italic"
              textAlign="center"
              textTransform="uppercase"
              marginTop="$14"
              marginBottom="$4"
              fontWeight="600"
              $gtTabletLandscape={{ size: '$10', fontWeight: '600' }}
              $gtMobile={{ size: '$9', fontWeight: '600' }}
              $mobile={{ marginTop: '$9', marginBottom: '$3', size: '$7', fontWeight: '600' }}>
              {bilt.content.title}
            </H1>
            <View>
              <Text textAlign="center" fontSize="$5" $mobile={{ fontSize: '$4' }}>
                {bilt.content.description}
              </Text>
            </View>
            <PointsPicker
              balance={balance}
              title={getString('bilt.landing.balance.title').replace('{{points}}', formatPoints({ points: balance }))}
              renderDialogContent={(amount) => (
                <Text fontSize="$6">
                  {getString('bilt.transfer.modal.body')
                    .replace('{{points}}', formatPoints({ points: amount ?? 0 }))
                    .replace('{{bonus}}', `${BONUS_POINTS}`)}
                </Text>
              )}
            />
          </RewardInfoContent>
          <View marginTop="$5" gap="$5">
            <RewardCta>
              <RewardInfoContent>
                <ContentfulCopy content={bilt.content.redemption.method} />
              </RewardInfoContent>
            </RewardCta>
            <RewardInfoContent>
              <ContentfulCopy content={bilt.content.termsAndConditions} />
            </RewardInfoContent>
          </View>
        </RewardInfoWrapper>
      </Page>
    </>
  )
}

const steps: TextSegment[][] = [
  [{ text: 'Choose your points,', marks: [{ type: 'bold' }] }, { text: ' choose how many Bilt Points to transfer.' }],
  [{ text: 'Transfer,', marks: [{ type: 'bold' }] }, { text: ' hit the "Transfer" button to move them to Virgin Red.' }],
  [{ text: 'Get your reward,', marks: [{ type: 'bold' }] }, { text: ' redeem points for rewards instantly in Virgin Red.' }],
  [{ text: 'Combine & redeem,', marks: [{ type: 'bold' }] }, { text: ' use your boosted balance to unlock more rewards in Virgin Red.' }],
  [{ text: 'Enjoy!' }],
]

const terms = [
  'Transfers are instant and cannot be reversed once completed.',
  'You must be a registered member of both Bilt and Virgin Red to transfer points.',
  'Points must be transferred in increments of 1,000 or more.',
  'Transferred Virgin Points can only be used on eligible rewards available in your country.',
  'Voucher codes are valid for one use only and must be shown to a team member at participating outlets.',
  'Codes are valid until the expiry date shown. Expired codes will not be reissued.',
  'Only one voucher can be used per person, per visit.',
  'Rewards are subject to availability and may change without notice.',
  'Some rewards are only valid for takeaway and cannot be used in-store.',
  'Virgin Red codes may not be valid at all locations.',
  'Vouchers are non-refundable, non-transferable, and cannot be exchanged for cash.',
  'Rewards do not count toward any third-party loyalty or stamp schemes.',
]

const bilt: {
  content: {
    title: string
    description: string
    brandDetails: { brandName: string; brandLogo: string }
    imageUrl: string
    redemption: { method: Document }
    termsAndConditions: Document
  }
} = {
  content: {
    title: 'Transfer Bilt points to your Virgin Red account',
    description:
      'Use all your Bilt and Virgin Points together, or split them between accounts for even more flexibility, your points will transfer instantly.',
    brandDetails: { brandName: 'Bilt', brandLogo: `${PUBLIC_URL}/img/bilt/logo-image.png` },
    imageUrl: `${PUBLIC_URL}/img/bilt/landing-image.png`,
    redemption: {
      method: {
        data: {},
        nodeType: BLOCKS.DOCUMENT,
        content: [
          createHeading('How it works'),
          {
            data: {},
            nodeType: BLOCKS.OL_LIST,
            content: steps.map((step) => ({
              data: {},
              nodeType: BLOCKS.LIST_ITEM,
              content: [createParagraph(step)],
            })),
          },
          createParagraph([
            {
              text: 'This reward is brought to you by Virgin Red, the gateway to a world of rewards from across the Virgin Family and beyond.',
              marks: [{ type: 'italic' }],
            },
          ]),
        ],
      },
    },
    termsAndConditions: {
      data: {},
      nodeType: BLOCKS.DOCUMENT,
      content: [
        createHeading('Terms and Conditions'),
        {
          data: {},
          nodeType: BLOCKS.UL_LIST,
          content: terms.map((term) => ({
            data: {},
            nodeType: BLOCKS.LIST_ITEM,
            content: [createParagraph([{ text: term }])],
          })),
        },
      ],
    },
  },
}

type TextSegment = {
  text: string
  marks?: { type: 'bold' | 'italic' }[]
}

function createParagraph(segments: TextSegment[]): TopLevelBlock {
  return {
    data: {},
    nodeType: BLOCKS.PARAGRAPH,
    content: segments.map(({ text, marks = [] }) => ({
      data: {},
      nodeType: 'text',
      value: text,
      marks,
    })),
  }
}

function createHeading(text: string): TopLevelBlock {
  return {
    data: {},
    nodeType: BLOCKS.HEADING_3,
    content: [
      {
        data: {},
        nodeType: 'text',
        value: text,
        marks: [],
      },
    ],
  }
}
